import React from "react";
import { Box, FormHelperText, MenuItem, TextField } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";
import { format, isValid, parseISO } from "date-fns";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton.jsx";
import DateInput from "../../components/DateInput";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { CREATE_PUBLIC_HOLIDAY } from "../../graph/holidayManagement/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const jurisdictions = [
  "ENGLAND",
  "WALES",
  "SCOTLAND",
  "NORTHERN_IRELAND",
];

const validationSchema = yup.object({
  jurisdiction: yup
    .string()
    .oneOf(jurisdictions)
    .required("Select a jurisdiction"),
  name: yup.string().trim().required("Holiday name is required"),
  date: yup.string().required("Holiday date is required"),
  observedDate: yup.string().nullable(),
  year: yup.number().integer().required("Year is required"),
  source: yup.string().nullable(),
});

const CreatePublicHolidayForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const redirectPath =
    location.state?.redirectPath || "/managePublicHolidays";

  const initialValues = {
    jurisdiction: location.state?.jurisdiction || "WALES",
    name: "",
    date: "",
    observedDate: "",
    year: location.state?.year || new Date().getFullYear(),
    source: "",
  };

  // DateInput emits an ISO timestamp; the GraphQL Date scalar needs YYYY-MM-DD.
  const setGraphQLDate = (setFieldValue) => (name, value) => {
    if (!value) {
      setFieldValue(name, "");
      return;
    }

    const parsedDate = parseISO(value);
    setFieldValue(
      name,
      isValid(parsedDate) ? format(parsedDate, "yyyy-MM-dd") : ""
    );
  };

  const handleSubmit = async (values) => {
    const input = {
      jurisdiction: values.jurisdiction,
      name: values.name.trim(),
      date: values.date,
      observedDate: values.observedDate || null,
      year: Number(values.year),
      source: values.source?.trim() || null,
    };

    try {
      await client.request(CREATE_PUBLIC_HOLIDAY, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Public holiday created successfully",
      });

      navigate(redirectPath, {
        state: {
          jurisdiction: input.jurisdiction,
          year: input.year,
        },
      });
    } catch (err) {
      console.error("Failed to create public holiday:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to create public holiday",
      });
    }
  };

  return (
    <Box>
      <Header
        title="CREATE PUBLIC HOLIDAY"
        subtitle="Add a public holiday to the shared calendar"
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                select
                label="Jurisdiction"
                name="jurisdiction"
                value={values.jurisdiction}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.jurisdiction && !!errors.jurisdiction}
                helperText={touched.jurisdiction && errors.jurisdiction}
                sx={{ gridColumn: "span 2" }}
              >
                {jurisdictions.map((jurisdiction) => (
                  <MenuItem key={jurisdiction} value={jurisdiction}>
                    {jurisdiction.replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Holiday Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 2" }}
              />

              <Box sx={{ gridColumn: "span 2" }}>
                <DateInput
                  label="Date"
                  name="date"
                  value={values.date}
                  setFieldValue={setGraphQLDate(setFieldValue)}
                  error={errors.date}
                  touched={touched.date}
                />
              </Box>

              <Box sx={{ gridColumn: "span 2" }}>
                <DateInput
                  label="Observed Date"
                  name="observedDate"
                  value={values.observedDate}
                  setFieldValue={setGraphQLDate(setFieldValue)}
                  error={errors.observedDate}
                  touched={touched.observedDate}
                />
                <FormHelperText>
                  The substitute day off when the holiday is observed on a
                  different date, such as when it falls at a weekend. Leave
                  blank if there is no substitute date.
                </FormHelperText>
              </Box>

              <TextField
                label="Year"
                name="year"
                type="number"
                value={values.year}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.year && !!errors.year}
                helperText={touched.year && errors.year}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Source"
                name="source"
                value={values.source}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText="Where this holiday information came from, such as GOV.UK or an official calendar URL."
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton type="submit" tone="success">
                Create Public Holiday
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreatePublicHolidayForm;